import { serverFetch, type createSdkForServer } from "./server"
import type { ServerConnection } from "@/context/server"
import { expectResponse } from "./http"

export async function requestEventBarrier(server: ServerConnection.HttpBase, token: string, signal: AbortSignal, fetch?: typeof globalThis.fetch) {
  const response = await serverFetch(server, "/global/event/barrier", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
    signal,
  }, { fetch })
  await expectResponse(response, { error: "Unable to synchronize the event stream" })
}

/** A change to the chats whose transcript events an open event stream receives. */
export type EventWatchChange = {
  token: string
  /** `since`: the event through which the client holds every event of the chat; `resume`: it watched the chat through there. */
  add: Array<{ session: string; since?: string; resume?: boolean }>
  remove: string[]
}

/**
 * Changes the chats an open event stream watches; the stream confirms with a
 * `server.watched` marker carrying the token. Resolves false when the stream
 * has ended.
 */
export async function requestEventWatch(server: ServerConnection.HttpBase, stream: string, change: EventWatchChange, signal: AbortSignal, fetch?: typeof globalThis.fetch) {
  const response = await serverFetch(server, "/global/event/watch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ stream, ...change }),
    signal,
  }, { fetch })
  await expectResponse(response, { allow: [404], error: "Unable to follow chats on the event stream" })
  return response.status !== 404
}

export function observeEventStreamResponse(established: (server: ServerConnection.HttpBase) => void) {
  return (response: Response, server: ServerConnection.HttpBase, signal?: AbortSignal | null) => {
    if (response.ok && !signal?.aborted) established(server)
  }
}

type GlobalEventSDK = ReturnType<typeof createSdkForServer>

type GlobalEventStreamOptions = {
  signal: AbortSignal
  query: {
    directories: string
    include: "summary"
    /** Asks for slow heartbeats. */
    heartbeat?: "idle"
    /** The client's link is slow: the runtime batches streamed text into fewer writes. */
    link?: "slow"
  }
  headers?: Record<string, string>
  sseMaxRetryAttempts: number
  sseDefaultRetryDelay: number
  onSseError: (error: unknown) => void
  onSseEvent: (event: { id?: string }) => void
}

/** Typed contract for Mux's filtered extension to the vendored global event endpoint. */
export function openGlobalEventStream(sdk: GlobalEventSDK, options: GlobalEventStreamOptions) {
  return sdk.global.event(options as unknown as Parameters<GlobalEventSDK["global"]["event"]>[0])
}
